'use client';

/**
 * Front-desk calls centre.
 *
 * Renders the property-wide call state — who is ringing, who is waiting —
 * and lets any available staff member claim a call that is ringing someone
 * else.
 *
 * This exists because `CentralCallsPanel` was written but never mounted: the
 * gateway broadcast `call:state` on every change and nothing displayed it, so
 * a call ringing an occupied colleague was invisible to everyone else and
 * simply rang out. The panel, `QueueStatusBadge` and `CallRegistryBadge` were
 * all complete components with no render path.
 *
 * Pickup goes through the gateway socket (`store.pickupCall`), not the REST
 * endpoint — see the note on `pickupCall` for why the REST route cannot
 * actually connect a call.
 */

import { useCallback } from 'react';

import { useStaffCallStore } from '../hooks/useStaffCallClient';
import { CentralCallsPanel } from './CentralCallsPanel';
import type { RingingCallEntry } from '../types/calls.types';

interface CallsCenterProps {
  /** The signed-in staff member, used as the callee when claiming a call. */
  staffId: string;
}

export function CallsCenter({ staffId }: CallsCenterProps) {
  const callState = useStaffCallStore((s) => s.callState);
  const pickupCall = useStaffCallStore((s) => s.pickupCall);
  const activeCall = useStaffCallStore((s) => s.activeCall);
  const state = useStaffCallStore((s) => s.state);

  const busy = state === 'connected' || state === 'connected-muted' || state === 'connecting';

  const handlePickup = useCallback(
    (callId: string) => {
      const entry = callState?.ringing.find((r) => r.callId === callId);
      pickupCall(callId, staffId, {
        callerName: entry?.callerName,
        bookingId: entry?.bookingId,
      });
    },
    [callState, pickupCall, staffId],
  );

  // Nothing to show before the first `call:state` arrives.
  if (!callState) return null;

  // Hide calls this staff member is already on — offering "Pick up" for the
  // call in your own ear is nonsense, and taking it would drop it.
  const ringing: RingingCallEntry[] = callState.ringing.filter(
    (r) => r.callId !== activeCall?.callId,
  );

  if (ringing.length === 0 && callState.queueDepth === 0) return null;

  return (
    <CentralCallsPanel
      ringingCalls={ringing}
      queueDepth={callState.queueDepth}
      onPickup={handlePickup}
      // Mid-call the list stays visible — the queue depth matters even then —
      // but Pick up is disabled and explains itself, rather than looking live
      // and silently doing nothing.
      disabled={busy}
    />
  );
}
